import React from 'react';
import axios from 'axios';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { MdClose, MdUploadFile, MdInsertDriveFile } from 'react-icons/md';
import { HiExternalLink, HiPencil } from 'react-icons/hi';
import toastWrapper from '../../../extras/toastify';
import {
  fetchHandoutsForClassroom,
  generateAwsClientLink,
  updateHandoutsForSpecificClassroom,
  updateHandoutsForSpecificGroup,
} from '../../../services/apiService';

function HandoutModal({ showModal, setShowModal, data, refreshData, handoutLinkAvailable, activeTab }) {
  const fileInputRef = React.useRef(null);
  const [file, setFile] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(handoutLinkAvailable);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type === 'application/pdf') setFile(dropped);
  };

  const uploadFile = async () => {
    const apiData = { activityId: data?.activityId, fileFormat: 'pdf' };
    const res = await generateAwsClientLink(apiData);
    const { clientUrl, itemKey } = res;
    const resforFileUpload = await axios.put(clientUrl, file, {
      headers: { 'Content-Type': 'application/pdf' },
    });
    if (resforFileUpload.status === 200) {
      const bodyData = {
        activityId: data?.activityId,
        handout: { type: 's3', link: itemKey },
      };
      if (activeTab === 'LSS') {
        await updateHandoutsForSpecificClassroom(bodyData);
      } else {
        await updateHandoutsForSpecificGroup(bodyData);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return toastWrapper('warning', 'Please select a file', 'top-right');
    setIsUploading(true);
    try {
      await uploadFile();
      toastWrapper('success', 'Handout uploaded successfully', 'top-right');
      setShowModal(false);
      await refreshData();
    } catch (error) {
      console.error(error);
      toastWrapper('error', "Couldn't upload handout", 'top-right');
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewHandout = async () => {
    try {
      const res = await fetchHandoutsForClassroom(data?.classActivityRecording?.handout?.link);
      window.open(res?.signedUrl, '_blank');
    } catch (error) {
      console.error(error);
      toastWrapper('error', 'Could not open handout', 'top-right');
    }
  };

  const primaryBtn = {
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: isUploading ? '#818CF8' : '#4F46E5',
    border: 'none',
    borderRadius: 4,
    padding: '9px 20px',
    cursor: isUploading ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    opacity: isUploading ? 0.7 : 1,
    transition: 'opacity 150ms ease',
  };

  const ghostBtn = {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '9px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  return (
    <Dialog
      open={showModal}
      onClose={() => setShowModal(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px 14px',
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>
          {isEdit ? 'Upload Handout' : 'Handout'}
        </h2>
        <button
          onClick={() => setShowModal(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4, borderRadius: 4, display: 'flex' }}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>
      </div>

      <DialogContent style={{ padding: '20px 24px 24px' }}>
        {!isEdit ? (
          /* View state — handout exists */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 4px' }}>
              A handout is already uploaded for this session.
            </p>
            <button onClick={handleViewHandout} style={primaryBtn}>
              <HiExternalLink size={15} />
              View Handout
            </button>
            <button onClick={() => setIsEdit(true)} style={ghostBtn}>
              <HiPencil size={14} />
              Replace Handout
            </button>
          </div>
        ) : (
          /* Upload state */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Drop zone */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${file ? '#4F46E5' : '#E5E7EB'}`,
                borderRadius: 8,
                backgroundColor: file ? '#EEF2FF' : '#F9FAFB',
                padding: '28px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                transition: 'border-color 150ms ease, background-color 150ms ease',
              }}
            >
              {file ? (
                <>
                  <MdInsertDriveFile size={32} style={{ color: '#4F46E5' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#4F46E5', wordBreak: 'break-all', textAlign: 'center' }}>
                    {file.name}
                  </span>
                  <span style={{ fontSize: 11.5, color: '#6B7280' }}>
                    {(file.size / 1024).toFixed(1)} KB · Click to change
                  </span>
                </>
              ) : (
                <>
                  <MdUploadFile size={32} style={{ color: '#9CA3AF' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    Click or drag to upload
                  </span>
                  <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>PDF only</span>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setFile(null); setIsEdit(false); }} style={ghostBtn}>
                Cancel
              </button>
              <button onClick={handleUpload} disabled={isUploading} style={primaryBtn}>
                {isUploading ? 'Uploading…' : 'Upload Handout'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default HandoutModal;
